import { Fragment, useMemo, useContext } from 'react';
import * as Table from '@/components/Table';
import { ProjectDetailsContext } from '@/screens/Project.Main/Context';
import {
  ColumnActions,
  ColumnOrdinal,
  ColumnColor,
  ColumnTitle,
  ColumnCategory,
  ColumnStatus,
  ColumnSummary,
} from './Column';
import { UpdateTagContext } from './Context';
import { useUpdateProjectTagMutation } from './hooks/useUpdateProjectTagMutation';
import type { TagItem } from './interfaces';
import { ProjectTagRow } from './ProjectTagRow';
import styles from './style/TagsTable.module.css';

type Props = {
  tags: TagItem[];
};

export const TagsTable = ({ tags }: Props) => {
  const { query } = useContext(ProjectDetailsContext);
  const projectId = query.data?.project?.id;
  const updateTagMutation = useUpdateProjectTagMutation(projectId || 0);

  // Sort tags by ordinal for consistent display
  const sortedTags = useMemo(() => {
    return [...tags].sort((a, b) => {
      const aOrdinal = a.ordinal || Number.MAX_SAFE_INTEGER;
      const bOrdinal = b.ordinal || Number.MAX_SAFE_INTEGER;
      return aOrdinal - bOrdinal;
    });
  }, [tags]);

  return (
    <Table.Layout.Box>
      <Table.Layout.Header title="Tags" />
      
      <Table.Root>
        <Table.Header.THead>
          <Table.Header.TR>
            <ColumnOrdinal isHeader={true}>
              <span className={styles.headerText}>Order</span>
            </ColumnOrdinal>
            <ColumnColor isHeader={true}>
              <span className={styles.headerText}>Color</span>
            </ColumnColor>
            <ColumnTitle isHeader={true}>
              <span className={styles.headerText}>Tag Title</span>
            </ColumnTitle>
            <ColumnCategory isHeader={true}>
              <span className={styles.headerText}>Tag Category</span>
            </ColumnCategory>
            <ColumnStatus isHeader={true}>
              <span className={styles.headerText}>Tag Status</span>
            </ColumnStatus>
            <ColumnSummary isHeader={true}>
              <span className={styles.headerText}>Include Summary</span>
            </ColumnSummary>
            <ColumnActions isHeader={true} />
          </Table.Header.TR>
        </Table.Header.THead>

        <Table.Body.TBody>
          {sortedTags.map((tag) => (
            <Fragment key={tag.id}>
              <UpdateTagContext value={updateTagMutation}>
                <ProjectTagRow
                  key={tag.id}
                  tag={tag} />
              </UpdateTagContext>
              {tag.children.length > 0 && tag.children.map(child => (
                <Fragment key={child.id}>
                  <UpdateTagContext value={updateTagMutation}>
                    <ProjectTagRow
                      key={child.id}
                      tag={{ ...child, children: [] }} />
                  </UpdateTagContext>
                </Fragment>
              ))}
            </Fragment>
          ))}
        </Table.Body.TBody>
      </Table.Root>
    </Table.Layout.Box>
  );
};
